import * as Sentry from '@sentry/react'
import type { AjaxConfig } from 'rxjs/internal/ajax/types'
import { Observable, tap } from 'rxjs'
import type { AjaxResponse } from 'rxjs/ajax'
import { ajax } from 'rxjs/ajax'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import Actions, { Store } from '../Flux/index.js'
import { authenticationSlice } from '../Flux/Slice/Authentication/AuthenticationSlice.js'
import { assertNotMarkup } from './markupGuard.js'

function traceHeadersFromSpan(span?: Sentry.Span | null) {
  if (!span) return {}
  return {
    'sentry-trace': Sentry.spanToTraceHeader(span),
    baggage: Sentry.spanToBaggageHeader(span)
  } as const
}

export default function sentryTraceHeaders(): { baggage?: string; 'sentry-trace'?: string } {
  const activeSpan = Sentry.getActiveSpan()
  if (activeSpan) {
    const rootSpan = Sentry.getRootSpan(activeSpan)
    return {
      baggage: Sentry.spanToBaggageHeader(rootSpan),
      'sentry-trace': Sentry.spanToTraceHeader(rootSpan)
    }
  } else {
    return {}
  }
}

export function tracedAjax<T>(config: AjaxConfig): Observable<AjaxResponse<T>> {
  return new Observable<AjaxResponse<T>>((subscriber) => {
    const method = (config.method || 'GET').toUpperCase()
    const name = `${method} ${config.url}`

    let teardown: () => void = () => {}

    Sentry.startSpanManual({ name, op: 'http.client' }, (span, finish) => {
      // build headers *inside* the span callback so they belong to this span
      const headers = {
        ...config.headers,
        ...traceHeadersFromSpan(span)
      }

      const sub = ajax<T>({ ...config, headers })
        .pipe(
          tap((response) => {
            const refreshed = response?.xhr?.getResponseHeader('X-Token-Refreshed')
            if (refreshed === 'true') {
              console.info('Backend has refreshed tokens, refreshing user')
              Store.dispatch(Actions[authenticationSlice.name].updateUserIfPresent())
            }
          })
        )
        .subscribe({
          next: (v) => subscriber.next(v),
          error: (e) => {
            span?.setStatus({
              code: 2,
              message: 'internal_error'
            })
            finish()
            subscriber.error(e)
          },
          complete: () => {
            finish()
            subscriber.complete()
          }
        })

      // provide teardown to the Observable
      teardown = () => {
        try {
          sub.unsubscribe()
        } finally {
          finish()
        }
      }
    })

    return teardown
  })
}

export const tracingAxios = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: async function <T = any>(url: string, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const name = `GET ${url}`
    return Sentry.startSpan({ name, op: 'http.client' }, async () => {
      return await handleResponse(
        axios.get<T, AxiosResponse<T>>(url, {
          ...config,
          headers: { ...(config.headers || {}), ...sentryTraceHeaders() }
        })
      )
    })
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: async function <T = any, D = any>(url: string, data: D, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const name = `POST ${url}`
    return Sentry.startSpan({ name, op: 'http.client' }, async () => {
      return await handleResponse(
        axios.post<T, AxiosResponse<T>, D>(url, data, {
          ...config,
          headers: { ...(config.headers || {}), ...sentryTraceHeaders() }
        })
      )
    })
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: async function <T = any, D = any>(url: string, data: D, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const name = `PUT ${url}`
    return Sentry.startSpan({ name, op: 'http.client' }, async () => {
      return await handleResponse(
        axios.put<T, AxiosResponse<T>, D>(url, data, {
          ...config,
          headers: { ...(config.headers || {}), ...sentryTraceHeaders() }
        })
      )
    })
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete: async function <T = any, D = any>(url: string, config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const name = `DELETE ${url}`
    return Sentry.startSpan({ name, op: 'http.client' }, async () => {
      return await handleResponse(
        axios.delete<T, AxiosResponse<T>, D>(url, {
          ...config,
          headers: { ...(config.headers || {}), ...sentryTraceHeaders() }
        })
      )
    })
  }
}

async function handleResponse<T>(responsePromise: Promise<AxiosResponse<T>>) {
  const response = await responsePromise
  const refreshed = response.headers['x-token-refreshed']
  if (refreshed === 'true') {
    console.info('Backend has refreshed tokens (axios), refreshing user')
    Store.dispatch(Actions[authenticationSlice.name].updateUserIfPresent())
  } else if (refreshed != null) {
    console.warn('Found refresh header in axios, but value was wrong', refreshed)
  }
  assertNotMarkup(response.config.url, response.status, response.headers['content-type'])
  return response
}
